import { useState } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import type { ShopSummary } from '../../api/types.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { ConfirmDialog } from '../../components/ConfirmDialog.tsx';
import { QuietButton } from '../../components/QuietButton.tsx';
import { formatDateTime } from '../../format.ts';
import { palette } from '../../theme.ts';
import { CopyableValue } from './CopyableValue.tsx';

const CODE_STATUS = {
  outstanding: { label: 'Outstanding', color: 'warning' },
  used: { label: 'Used', color: 'success' },
  invalidated: { label: 'Invalidated', color: 'default' },
  expired: { label: 'Expired', color: 'error' },
} as const;

export interface ShopEnrollmentSectionProps {
  shop: ShopSummary;
  onMutated: () => void;
}

export function ShopEnrollmentSection({ shop, onMutated }: Readonly<ShopEnrollmentSectionProps>): React.JSX.Element {
  const { api } = useAuth();
  const [issueOpen, setIssueOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  return (
    <Paper sx={{ p: 2.5 }}>
      <Typography variant="h6" gutterBottom>
        Registration
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: 2,
          mb: 2,
        }}
      >
        <CopyableValue label="Shop code" value={shop.shop_code} ariaLabel="shop-code" copyLabel="Copy shop code" />
        {shop.latest_enrollment_code?.code && (
          <CopyableValue
            label="Registration code"
            value={shop.latest_enrollment_code.code}
            ariaLabel="registration-code"
            copyLabel="Copy registration code"
          />
        )}
      </Box>
      {shop.latest_enrollment_code ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
            gap: 2,
            p: 2,
            mb: 2,
            border: `1px solid ${palette.quietBorder}`,
            borderRadius: 1,
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: palette.textSecondary, display: 'block', mb: 0.5 }}>
              Latest registration code
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={CODE_STATUS[shop.latest_enrollment_code.status].label}
              color={
                CODE_STATUS[shop.latest_enrollment_code.status].color === 'default'
                  ? undefined
                  : CODE_STATUS[shop.latest_enrollment_code.status].color
              }
            />
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: palette.textSecondary }}>
              Issued
            </Typography>
            <Typography>{formatDateTime(shop.latest_enrollment_code.issued_at)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: palette.textSecondary }}>
              Expires
            </Typography>
            <Typography>{formatDateTime(shop.latest_enrollment_code.expires_at)}</Typography>
          </Box>
        </Box>
      ) : (
        <Typography variant="body2" sx={{ color: palette.textSecondary, mb: 2 }}>
          No registration code has been issued.
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <QuietButton onClick={() => setIssueOpen(true)}>
          Issue new registration code
        </QuietButton>
        <QuietButton
          onClick={() => setResetOpen(true)}
          sx={{ color: 'error.main', borderColor: 'error.main' }}
        >
          Reset keys
        </QuietButton>
      </Box>

      <ConfirmDialog
        open={issueOpen}
        title="Issue new registration code"
        body="This invalidates any outstanding registration code for this shop. Continue?"
        confirmLabel="Issue code"
        confirmColor="primary"
        onConfirm={async () => {
          await api.shops.newEnrollmentCode(shop.id);
        }}
        onClose={() => setIssueOpen(false)}
        onSuccess={onMutated}
      />

      <ConfirmDialog
        open={resetOpen}
        title="Reset keys"
        body="This revokes the shop's registered key -- the laptop stops syncing until it is registered again with the new code. Use this for a stolen or replaced laptop only."
        confirmLabel="Reset keys"
        confirmColor="error"
        onConfirm={async () => {
          await api.shops.keyReset(shop.id);
        }}
        onClose={() => setResetOpen(false)}
        onSuccess={onMutated}
      />
    </Paper>
  );
}

export default ShopEnrollmentSection;
